import { Event, OrganizationWithFeatures, Place } from "#api/enterprise-generated";
import * as React from "react";
import BreadCrumbs from "../../nav/bread-crumbs";
import SandwichMenu from "#resources/assets/svg/sandwich-menu.svg";
import { useSandwichMenu } from "#structure/providers/sandwich-menu";
import classNames from "classnames";
import { Notifications } from "#components/notifications";
import { SwitchLanguages } from "#components/switch-languages";
import { useModularization } from "#components/nav-bars/use-modularization";
import { SuportArea } from "#components/suport-area";
import { useZendeskUnreadCount } from "#resources/hooks/use-zendesk-support";
import { Badge } from "antd";
import Icon from "#components/icon";
import { AddResponsivenessTag } from "#components/layout/add-responsiveness";
import { useGetCurrentEmployee } from "#resources/hooks/integrations/auth/query";
import { useAnalytics } from "#structure/providers/analytics/analytics-provider";
import { contextLevelFromUrl } from "#resources/helpers/url/url";

const s = require("./header.scss");

export interface ICustomHeaderProps {
	environment?: string;
	message: {
		color?: string;
		text: string;
	};
	imageUrl?: string;
}

interface ComponentProps {
	place: Place | undefined;
	event: Event | undefined;
	organization: OrganizationWithFeatures | undefined;
	shouldIncludeOrganizationOnURL?: boolean;
	customHeader?: ICustomHeaderProps;
}

type Props = ComponentProps;

function CustomHeader(props: ICustomHeaderProps) {
	const { message, imageUrl, environment } = props;

	return (
		<>
			<div
				className={s.customText}
				style={{
					color: message.color || "#fff",
				}}
			>
				<span>{message.text}</span>
				{environment && <span className={s.environment}>{environment}</span>}
			</div>

			<img alt="custom-header-img" src={imageUrl} className={s.customImage} />
		</>
	);
}

function Header(props: Props) {
	const {
		event,
		organization,
		place,
		customHeader,
		shouldIncludeOrganizationOnURL,
	} = props;

	const placeName = place?.name;

	const { data: currentEmployee } = useGetCurrentEmployee({});

	const { sendEvent } = useAnalytics();
	const contextLevel = contextLevelFromUrl();

	const handleContactSupport = () => {
		sendEvent("SUPPORT", {
			actionType: "click",
			userName: currentEmployee?.name ?? "",
			userLogin: currentEmployee?.username ?? "",
			placeName: placeName ?? "",
			positionName: currentEmployee?.role?.name ?? "",
			dateUserAction: new Date().toISOString(),
			contextLevel,
		});
	};

	const { isOpenSandwichMenu, onChangeIsOpenSandwichMenu } = useSandwichMenu();

	const { showMenuV2: showSwitchLanguages } = useModularization();

	const [isOpenHelpChat, setIsOpenHelpChat] = React.useState(false);

	const unreadCount = useZendeskUnreadCount();

	return (
		<header className={s.header}>
			<AddResponsivenessTag />
			<button
				className={classNames({
					[s.sandwichButton]: true,
					[s.isOpenSandwichMenu]: isOpenSandwichMenu,
				})}
				onClick={() =>
					onChangeIsOpenSandwichMenu(oldIsOpenSandwichMenu => !oldIsOpenSandwichMenu)
				}
			>
				<SandwichMenu />
			</button>

			<div className={s.breadcrumbs}>
				<BreadCrumbs
					organization={organization}
					place={place}
					event={event}
					includeOrganizationOnUrl={shouldIncludeOrganizationOnURL}
				/>
			</div>
			<div className={s.customArea}>
				{customHeader && <CustomHeader {...customHeader} />}
			</div>
			{showSwitchLanguages && (
				<div className={s.switchLanguages}>
					<SwitchLanguages />
				</div>
			)}
			<div className={s.widgetsContainer}>
				<Notifications />
				<div className={s.helpChatContainer}>
					<Badge count={unreadCount} size="small">
						<Icon
							className={s.iconHelpChat}
							icon="help"
							onClick={() => {
								handleContactSupport();
								setIsOpenHelpChat(true);
							}}
						/>
					</Badge>
					<SuportArea
						type="desktop"
						isOpen={isOpenHelpChat}
						placeName={placeName ?? ""}
						handleChangeIsOpen={isOpen => setIsOpenHelpChat(isOpen)}
					/>
				</div>
			</div>
		</header>
	);
}

export default Header;
